import { Link } from "@/lib/router-compat";
import { ArrowRight } from "lucide-react";
import SiteNav, { BookingButton, WriteMeLink } from "@/components/site/SiteNav";
import SiteFooter from "@/components/site/SiteFooter";
import PageSeo from "@/components/site/PageSeo";
import LogoMarquee from "@/components/site/LogoMarquee";

export const BORDEAUX_PATH = "/fractional-cmo-bordeaux";
export const BORDEAUX_TITLE = "Fractional CMO à Bordeaux | Directeur marketing à temps partagé";
export const BORDEAUX_DESCRIPTION =
  "Un directeur marketing à temps partagé (Fractional CMO) pour les PME et ETI de Gironde : stratégie et pilotage, sans CDI. Disponible sous 2 semaines.";
const H1 = "Fractional CMO à Bordeaux : direction marketing opérationnelle sans recrutement CDI";
const URL = `https://www.cmopartner.fr${BORDEAUX_PATH}`;
const AREAS = ["Bordeaux", "Mérignac", "Pessac", "Bègles", "Arcachon", "Gironde", "Nouvelle-Aquitaine", "France"];

export const BORDEAUX_FAQ = [
  {
    q: "Quel est le délai pour démarrer une mission ?",
    a: "Disponibilité sous 2 semaines. Un appel de 30 minutes permet d'identifier vos leviers prioritaires et de confirmer la pertinence de la mission avant tout engagement.",
  },
  {
    q: "Combien de jours par mois consacrez-vous à chaque mission ?",
    a: "Entre 2 et 12 jours par mois selon le format : léger (2 à 4 jours), standard (4 à 6 jours) ou intensif (8 à 12 jours). Le format est défini au départ et ajustable, par exemple un mois intensif de mise en place puis un accompagnement léger.",
  },
  {
    q: "Intervenez-vous en présentiel à Bordeaux ?",
    a: "Oui. Les phases de démarrage (audit, premier cycle) incluent systématiquement des journées en présentiel en Gironde. La suite de la mission est hybride selon vos besoins.",
  },
  {
    q: "Travaillez-vous avec des entreprises hors de Bordeaux ?",
    a: "Oui. Le mode hybride, à distance avec du présentiel ponctuel, fonctionne pour toute la France et à l'international. La proximité bordelaise est un avantage pour les entreprises de la métropole, pas une contrainte pour les autres.",
  },
  {
    q: "Quels sont vos tarifs ?",
    a: "Le tarif dépend du format retenu (nombre de jours par mois) et de la durée de la mission. Il est établi sur devis après un appel de cadrage. Pour les ordres de grandeur, consultez notre guide des tarifs d'un Fractional CMO.",
  },
  {
    q: "Avec quelles tailles d'entreprises travaillez-vous ?",
    a: "Principalement des PME et ETI de 10 à 300 personnes, en France et à l'international. Secteurs de prédilection : SaaS B2B, e-commerce B2C, industrie et services.",
  },
  {
    q: "Directeur marketing à temps partagé à Bordeaux : comment ça marche ?",
    a: "Un directeur marketing à temps partagé prend la direction de votre marketing quelques jours par mois, sans recrutement en CDI. À Bordeaux et en Gironde, les phases de démarrage incluent du présentiel ; la suite de la mission est hybride, avec des points réguliers à distance. Le format, de 2 à 12 jours par mois, est défini ensemble après un premier échange.",
  },
];

const provider = {
  "@type": "ProfessionalService",
  "@id": `${URL}#service-provider`,
  name: "CMO Partner — Fractional CMO à Bordeaux",
  url: URL,
  description: BORDEAUX_DESCRIPTION,
  areaServed: AREAS,
  address: {
    "@type": "PostalAddress",
    addressLocality: "Bordeaux",
    addressRegion: "Nouvelle-Aquitaine",
    addressCountry: "FR",
  },
  founder: { "@type": "Person", name: "Tiphany Rigaudias", jobTitle: ["Fractional CMO", "Directrice marketing externalisée", "Directrice digitale externalisée"], knowsAbout: ["Marketing B2B", "CRM", "Marketing automation", "IA appliquée au marketing", "Formation des équipes marketing"], url: "https://www.cmopartner.fr/a-propos" },
  knowsLanguage: ["fr", "en"],
  sameAs: ["https://linkedin.com/in/tiphanyrigaudias"],
};

export const BORDEAUX_SCHEMA = [
  { "@context": "https://schema.org", ...provider },
  {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Fractional CMO",
    provider: { "@id": provider["@id"] },
    areaServed: AREAS,
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: BORDEAUX_FAQ.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Accueil", item: "https://www.cmopartner.fr/" },
      { "@type": "ListItem", position: 2, name: "Fractional CMO à Bordeaux", item: URL },
    ],
  },
];

const H2 = ({ children }: { children: string }) => (
  <h2 className="text-3xl font-bold leading-tight md:text-4xl">
    {children}
    <span className="text-primary">.</span>
  </h2>
);

const steps = [
  {
    label: "Semaines 1 et 2",
    title: "Audit et diagnostic",
    text: "Revue de votre écosystème marketing existant : CRM, canaux, contenus, pipeline. Identification des 3 leviers prioritaires. Remise d'un diagnostic écrit.",
  },
  {
    label: "Mois 1",
    title: "Premier cycle",
    text: "Déploiement des actions prioritaires : segmentation, séquence d'activation, scoring des leads, mise en place des indicateurs. Premiers résultats mesurables.",
  },
  {
    label: "Mois 2 et 3",
    title: "Industrialisation",
    text: "Automatisation de ce qui fonctionne, formation de l'équipe, construction d'un système marketing qui tourne sans dépendre en permanence du Fractional CMO.",
  },
];

const missions = [
  {
    title: "Growth Architecture",
    to: "/services/growth-architecture",
    text: "Définition du modèle d'acquisition, tunnel de conversion, coordination des agences et prestataires. Pour les PME qui veulent passer d'une croissance intuitive à une croissance systématique.",
  },
  {
    title: "CRM & Lifecycle Marketing",
    to: "/services/crm-lifecycle",
    text: "Déploiement ou refonte HubSpot / Salesforce, automatisations email, scoring, alignement marketing-sales. Pour les PME qui veulent transformer leur CRM en machine à convertir.",
  },
  {
    title: "Fractional CMO B2C / E-commerce",
    to: "/fractional-cmo-b2c-ecommerce",
    text: "Tunnel d'acquisition, stratégie de fidélisation, coordination marketplace. Pour les marques B2C et e-commerce en croissance.",
  },
  {
    title: "IA & Agentic Marketing",
    to: "/services/ai-marketing",
    text: "Intégration des outils IA (personnalisation, contenu assisté, scoring prédictif) et formation des équipes. Pour les PME qui veulent industrialiser leur marketing sans multiplier les effectifs.",
  },
];

const formats = [
  ["Léger", "2 à 4 jours par mois", "Définition de la stratégie d'acquisition et pilotage mensuel avec le dirigeant"],
  ["Standard", "4 à 6 jours par mois", "Pilotage du plan marketing, management de l'équipe et des prestataires"],
  ["Intensif", "8 à 12 jours par mois", "Mise en place des automatisations CRM et déploiement des outils"],
];

const results = [
  ["smsmode", "Growth Architecture + CRM", "+50 % de leads inbound, +25 % de ROI marketing, plus de 10 M€ de MRR supportés avec une équipe de plus de 10 personnes"],
  ["Lumivero", "Stratégie d'expansion", "+30 % de croissance du CA, 1 M€ de nouveaux marchés ouverts"],
];

const readNext = [
  { label: "Stratégie marketing pour PME à Bordeaux", to: "/blog/strategie-marketing-pme-bordeaux" },
  { label: "Implémenter un CRM à Bordeaux en 2026", to: "/blog/implementer-crm-bordeaux-hubspot-salesforce" },
  { label: "Directeur marketing externalisé", to: "/directeur-marketing-externalise" },
];

const Table = ({ head, rows }: { head: string[]; rows: string[][] }) => (
  <div className="mt-8 overflow-x-auto rounded-lg border border-border">
    <table className="w-full min-w-[600px] text-left text-sm md:text-base">
      <thead className="bg-secondary">
        <tr>
          {head.map((h) => (
            <th key={h} scope="col" className="px-5 py-4 font-semibold">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r[0]} className="border-t border-border">
            {r.map((cell, i) => (
              <td key={i} className={`px-5 py-4 align-top ${i === 0 ? "font-semibold" : "text-muted-foreground"}`}>{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const BordeauxPage = () => {
  const book = "Réserver un appel";
  return (
    <div className="min-h-screen bg-background text-foreground">
      <PageSeo path={BORDEAUX_PATH} title={BORDEAUX_TITLE} description={BORDEAUX_DESCRIPTION} />
      <SiteNav />
      <main>
        <section className="border-b border-border px-5 pb-16 pt-20 md:pb-20 md:pt-28">
          <div className="mx-auto max-w-4xl">
            <nav className="mb-8 text-sm text-muted-foreground" aria-label="Fil d'Ariane">
              <Link to="/" className="transition-colors hover:text-primary">Accueil</Link>
              <span className="px-2">/</span>
              <span className="text-foreground">Fractional CMO à Bordeaux</span>
            </nav>
            <h1 className="text-4xl font-bold leading-[1.1] md:text-6xl">
              {H1}
              <span className="text-primary">.</span>
            </h1>
            <p className="mt-8 text-lg leading-relaxed text-muted-foreground md:text-xl">
              Vous dirigez une PME ou une ETI en Gironde. Votre équipe marketing exécute, mais il manque un cap. Ou vous n'avez pas encore de fonction marketing structurée. En tant que{" "}
              <Link to="/directeur-marketing-externalise" className="text-foreground underline underline-offset-4 hover:text-primary">directeur marketing à temps partagé à Bordeaux</Link>
              , disponible sous 2 semaines, je prends la direction opérationnelle de votre marketing : stratégie, pilotage, diagnostic initial en 2 semaines, premières actions dès le premier mois.
            </p>
            <div className="mt-10">
              <BookingButton label="Prendre 30 minutes pour identifier vos leviers prioritaires" location="bordeaux_hero" large />
            </div>
          </div>
        </section>

        <LogoMarquee />

        <section className="px-5 py-20 md:py-24">
          <div className="mx-auto max-w-4xl space-y-20">
            <article>
              <H2>Pourquoi faire appel à un Fractional CMO à Bordeaux ?</H2>
              <p className="mt-6 text-lg text-muted-foreground">Les PME bordelaises font face à trois réalités :</p>
              <ul className="mt-6 space-y-3">
                {[
                  "Un CMO en CDI coûte 80 à 150 k€ par an en coût total employeur, et son recrutement prend souvent 4 à 6 mois.",
                  "Une agence exécute, mais ne remplace pas une direction stratégique.",
                  "Le tissu économique bordelais fonctionne beaucoup en réseau : la proximité physique reste un facteur de confiance.",
                ].map((b) => (
                  <li key={b} className="flex gap-3 text-lg leading-relaxed text-muted-foreground">
                    <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-primary" aria-hidden="true" />
                    {b}
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-lg leading-relaxed text-muted-foreground">
                La direction marketing à temps partagé répond aux trois : une expertise senior, une disponibilité rapide et une présence physique en Gironde.
              </p>
            </article>

            <article>
              <H2>Ce que je fais concrètement</H2>
              <div className="mt-8 space-y-6">
                {steps.map((s) => (
                  <div key={s.title} className="border-t border-border pt-6">
                    <span className="text-sm font-semibold text-primary">{s.label}</span>
                    <h3 className="mt-2 text-xl font-bold">{s.title}</h3>
                    <p className="mt-3 text-lg leading-relaxed text-muted-foreground">{s.text}</p>
                  </div>
                ))}
              </div>
              <div className="mt-10 rounded-lg bg-secondary p-7">
                <p className="text-lg font-semibold">30 minutes pour identifier vos leviers prioritaires.</p>
                <div className="mt-5"><BookingButton label={book} location="bordeaux_mid" /></div>
              </div>
            </article>

            <article>
              <H2>Missions disponibles à Bordeaux</H2>
              <div className="mt-8 grid gap-5 md:grid-cols-2">
                {missions.map((m) => (
                  <Link key={m.to} to={m.to} className="group rounded-lg border border-border p-6 transition-colors hover:border-primary">
                    <h3 className="text-xl font-bold">{m.title}</h3>
                    <p className="mt-3 leading-relaxed text-muted-foreground">{m.text}</p>
                    <ArrowRight className="mt-5 h-4 w-4 text-primary" aria-hidden="true" />
                  </Link>
                ))}
              </div>
            </article>

            <article>
              <H2>Trois formats d'accompagnement</H2>
              <Table head={["Format", "Rythme", "Exemple de mission"]} rows={formats} />
              <p className="mt-6 text-lg leading-relaxed text-muted-foreground">
                Le format évolue avec vos besoins : beaucoup de clients démarrent par un mois intensif pour l'audit et la mise en place, puis passent en accompagnement léger pour le pilotage. Tarif sur devis après un appel de cadrage de 30 minutes.
              </p>
            </article>

            <article>
              <H2>Résultats observés</H2>
              <Table head={["Client", "Mission", "Résultat"]} rows={results} />
              <p className="mt-6 text-lg leading-relaxed text-muted-foreground">
                16 ans d'expérience internationale, notamment chez Decathlon et Lavazza. Missions en présentiel à Bordeaux, Mérignac, Pessac, Bègles, Arcachon, et à distance dans toute la France.
              </p>
            </article>

            <article>
              <H2>Zone d'intervention</H2>
              <div className="mt-8 grid gap-5 md:grid-cols-2">
                <div className="rounded-lg bg-secondary p-6">
                  <h3 className="font-bold">Présence physique</h3>
                  <p className="mt-3 leading-relaxed text-muted-foreground">Bordeaux, Mérignac, Pessac, Bègles, Arcachon, Libourne, Gironde et Nouvelle-Aquitaine.</p>
                </div>
                <div className="rounded-lg bg-secondary p-6">
                  <h3 className="font-bold">À distance</h3>
                  <p className="mt-3 leading-relaxed text-muted-foreground">France entière et international (bilingue français / anglais).</p>
                </div>
              </div>
            </article>
          </div>
        </section>

        <section className="border-t border-border bg-secondary px-5 py-20">
          <div className="mx-auto max-w-4xl">
            <H2>FAQ : Fractional CMO à Bordeaux</H2>
            <div className="mt-10">
              {BORDEAUX_FAQ.map((f, i) => (
                <details key={f.q} className="group border-t border-border py-6">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-lg font-bold">
                    {f.q}
                    <span className="text-primary transition-transform group-open:rotate-45" aria-hidden="true">+</span>
                  </summary>
                  <p className="mt-3 leading-relaxed text-muted-foreground">
                    {i === 4 ? (
                      <>
                        Le tarif dépend du format retenu (nombre de jours par mois) et de la durée de la mission. Il est établi sur devis après un appel de cadrage. Pour les ordres de grandeur, consultez notre{" "}
                        <Link to="/blog/fractional-cmo-definition-missions-tarifs" className="text-foreground underline hover:text-primary">guide des tarifs d'un Fractional CMO</Link>.
                      </>
                    ) : (
                      f.a
                    )}
                  </p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="border-t border-border px-5 py-16">
          <div className="mx-auto max-w-4xl">
            <h2 className="text-xl font-bold">À lire aussi</h2>
            <div className="mt-6 grid gap-4 md:grid-cols-3">
              {readNext.map((r) => (
                <Link key={r.to} to={r.to} className="rounded-lg border border-border p-5 transition-colors hover:border-primary">
                  <span className="font-semibold">{r.label}</span>
                  <ArrowRight className="mt-4 h-4 w-4 text-primary" aria-hidden="true" />
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section id="booking" className="scroll-mt-16 bg-foreground px-5 py-24 text-center text-background md:py-28">
          <div className="mx-auto max-w-4xl">
            <h2 className="text-4xl font-bold md:text-5xl">Parlons de votre situation<span className="text-primary">.</span></h2>
            <p className="mt-6 text-lg text-background/60">30 minutes pour identifier vos leviers prioritaires.</p>
            <div className="mt-10"><BookingButton label={book} location="bordeaux_final" large /><WriteMeLink /></div>
            <p className="mt-6 text-sm text-background/50">Basée à Bordeaux · Présentiel en Gironde · Disponible sous 2 semaines</p>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
};

export default BordeauxPage;
